// src/components/auth/AuthShell.js
//
// Pass hosting/UX — the production auth front-door. A modern two-panel SaaS layout: a brand/story panel
// (real SAS Niseko photography under a dark gradient) on the left, and a clean authentication card on the
// right. Responsive: on phones the photo collapses to a shallow header so the form is immediately usable.
// This is presentation only — no auth logic lives here (the card is passed in as children).

import React from 'react';
import MotusLogo from '../shared/MotusLogo';

export const AUTH_T = {
  ink: '#1A1917', ink2: '#57534E', ink3: '#9E9B95',
  bg: '#FAFAF8', card: '#FFFFFF', border: '#E7E5E0', borderStrong: '#D6D3CD',
  blue: '#2563EB', blueHover: '#1D4FD7', focus: 'rgba(37,99,235,0.35)',
  f: "'DM Sans',system-ui,-apple-system,sans-serif",
  fD: "'Syne','DM Sans',sans-serif",
};

// Real SAS Niseko photography, served with the app (public/assets). WebP with a JPEG fallback; a dark
// gradient overlay guarantees readable text regardless of the exact frame.
const HERO = "image-set(url('/assets/sas-hero.webp') type('image/webp'), url('/assets/sas-hero.jpg') type('image/jpeg'))";
const HERO_FALLBACK = "url('/assets/sas-hero.webp'), url('/assets/sas-hero.jpg')";

const POINTS = [
  ['Schedule', 'Live operations and instructor availability'],
  ['Coach', 'Lessons, video and progression'],
  ['Grow', 'Client history and rebooking'],
];

export default function AuthShell({ children }) {
  return (
    <div className="auth-shell" style={{ minHeight: '100vh', display: 'flex', background: AUTH_T.bg, fontFamily: AUTH_T.f, color: AUTH_T.ink }}>
      <style>{`
        .auth-brand{position:relative;flex:0 0 56%;min-height:100vh;overflow:hidden;background:#12100F;}
        .auth-brand__img{position:absolute;inset:0;background-position:center;background-size:cover;
          background-image:${HERO_FALLBACK};background-image:${HERO};}
        .auth-brand__grad{position:absolute;inset:0;background:
          linear-gradient(180deg, rgba(12,16,26,0.55) 0%, rgba(12,16,26,0.30) 38%, rgba(10,12,18,0.82) 100%),
          radial-gradient(120% 80% at 15% 10%, rgba(37,99,235,0.28), transparent 55%);}
        .auth-brand__inner{position:relative;height:100%;min-height:100vh;display:flex;flex-direction:column;
          justify-content:space-between;padding:44px 52px;box-sizing:border-box;color:#fff;}
        .auth-card-wrap{flex:1 1 44%;display:flex;align-items:center;justify-content:center;padding:40px 24px;box-sizing:border-box;}
        .auth-card{width:100%;max-width:400px;}
        .auth-mobilehead{display:none;}
        @media (max-width: 900px){
          .auth-shell{flex-direction:column;}
          .auth-brand{display:none;}
          .auth-mobilehead{display:block;position:relative;height:150px;overflow:hidden;background:#12100F;}
          .auth-mobilehead__img{position:absolute;inset:0;background-position:center 35%;background-size:cover;
            background-image:${HERO_FALLBACK};background-image:${HERO};}
          .auth-mobilehead__grad{position:absolute;inset:0;background:linear-gradient(180deg,rgba(12,16,26,0.35),rgba(12,16,26,0.75));}
          .auth-mobilehead__inner{position:relative;height:100%;display:flex;align-items:flex-end;padding:16px 20px;}
          .auth-card-wrap{padding:26px 20px 48px;align-items:flex-start;}
        }
      `}</style>

      {/* LEFT — brand / story (desktop) */}
      <div className="auth-brand">
        <div className="auth-brand__img" />
        <div className="auth-brand__grad" />
        <div className="auth-brand__inner">
          <MotusLogo width={132} variant="dark" />
          <div>
            <h1 style={{ fontFamily: AUTH_T.fD, fontSize: 40, lineHeight: 1.05, fontWeight: 800, margin: '0 0 14px', letterSpacing: 0.2, maxWidth: 460 }}>
              Coaching and operations, connected.
            </h1>
            <p style={{ fontSize: 15.5, lineHeight: 1.6, color: 'rgba(255,255,255,0.86)', maxWidth: 420, margin: '0 0 26px' }}>
              One place to run the day, deliver better lessons and keep every client relationship moving.
            </p>
            <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
              {POINTS.map(([t, d]) => (
                <div key={t} style={{ maxWidth: 170 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#fff', marginBottom: 3 }}>{t}</div>
                  <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.7)', lineHeight: 1.45 }}>{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE — shallow header crop */}
      <div className="auth-mobilehead">
        <div className="auth-mobilehead__img" />
        <div className="auth-mobilehead__grad" />
        <div className="auth-mobilehead__inner"><MotusLogo width={104} variant="dark" /></div>
      </div>

      {/* RIGHT — auth card */}
      <div className="auth-card-wrap">
        <div className="auth-card">{children}</div>
      </div>
    </div>
  );
}
